<template>
  <div class="container-fluid pt-2 grouped-items-component">

    <!-- Top bar: Search + Create -->
    <div class="row mb-2 align-items-center">
      <div class="col-md-6 col-sm-12">
        <form @submit.prevent="getGroupedProducts" class="mb-0">
          <div class="input-group">
            <input
              type="search"
              class="form-control"
              v-model="searchItems"
              @input="onSearchInput"
              placeholder="Search grouped items..."
              aria-label="Search grouped items"
            />
            <div class="input-group-append">
              <button class="btn btn-outline-secondary" type="submit" title="Search">
                <i class="fa fa-search"></i>
              </button>
            </div>
          </div>
        </form>
      </div>

      <div class="col-md-6 col-sm-12 text-right">
        <button class="btn btn-outline-primary" @click="openCreateModal">
          <i class="fa fa-plus mr-1"></i> Create New Group Item
        </button>
      </div>
    </div>

    <!-- Results table -->
    <div class="mb-5">
      <b-table
        :items="items"
        :fields="fields"
        hover
        striped
        small
        responsive="sm"
        sticky-header="100vh"
        @row-clicked="onRowClick"
      >
        <template #cell(title)="data">
          <div class="d-flex flex-column">
            <strong class="text-primary">{{ data.item.title }}</strong>
            <small class="text-muted">{{ data.item.items_count }} items</small>
          </div>
        </template>

        <template #cell(selling_price)="data">
          <div>{{ formatCurrency(data.item.selling_price) }}</div>
        </template>

        <template #cell(wholesale)="data">
          <div v-if="companySettings.has_wholesale">{{ formatCurrency(data.item.wholesale) }}</div>
          <div v-else class="text-muted">—</div>
        </template>
      </b-table>
    </div>

    <!-- VIEW DETAILS MODAL -->
    <b-modal ref="itemDetailsModal" hide-footer size="lg" title="Group Item Details">
      <div v-if="selectedProduct">
        <h5 class="mb-2">{{ selectedProduct.allOtherItems.group_title }}</h5>
        <div class="mb-3">
          <span class="mr-3"><strong>Retail:</strong> {{ formatCurrency(selectedProduct.allOtherItems.cost) }}</span>
          <span v-if="companySettings.has_wholesale"><strong>Wholesale:</strong> {{ formatCurrency(selectedProduct.allOtherItems.wholesale) }}</span>
        </div>

        <table class="table table-sm">
          <tbody>
            <tr>
              <td>Items in Group</td>
              <td>{{ selectedProduct.allOtherItems.grouped_items.length }}</td>
            </tr>
            <tr>
              <td>Last Updated</td>
              <td>{{ formatDate(selectedProduct.allOtherItems.updated_at, 'hh:mm a DD-MM-YYYY') }}</td>
            </tr>
          </tbody>
        </table>

        <div class="d-flex justify-content-between mt-3">
          <div>
            <button v-if="canManageInventory" class="btn btn-danger btn-sm mr-2" @click="deleteProd(selectedProduct.allOtherItems.id)">
              Delete
            </button>
            <button v-if="canManageInventory" class="btn  btn-outline-dark btn-sm" @click="openEditModal">
              Edit
            </button>
          </div>
          <div>
            <button class="btn btn-secondary btn-sm" @click="$refs.itemDetailsModal.hide()">Close</button>
          </div>
        </div>

      </div>
      <div v-else class="text-center">Loading...</div>
    </b-modal>

    <!-- EDIT / CREATE MODAL (same form used for create & edit) -->
    <b-modal ref="groupModal" size="lg" :title="isEditing ? `Edit ${form.group_title}` : 'Create Group Item'" hide-footer>
      <form @submit.prevent="submitGroupForm" novalidate>
        <div class="form-row">
          <div class="form-group col-md-8">
            <label class="mb-1">Group Title</label>
            <input class="form-control" v-model="form.group_title" required placeholder="e.g. Breakfast Combo" />
          </div>

          <div class="form-group col-md-4">
            <label class="mb-1">Status</label>
            <select class="form-control" v-model="form.is_active">
              <option :value="true">Active</option>
              <option :value="false">Inactive</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-4">
            <label class="mb-1">Selling Price</label>
            <input type="number" step="any" class="form-control" v-model.number="form.cost" placeholder="e.g. 1500" />
          </div>

          <div class="form-group col-md-4">
            <label class="mb-1">Wholesale Price</label>
            <input type="number" step="any" class="form-control" v-model.number="form.wholesale" placeholder="e.g. 1200" />
          </div>

          <div class="form-group col-md-4">
            <label class="mb-1">VAT Status</label>
            <select class="form-control" v-model="form.vat_category">
              <option value="standard">Has VAT (16%)</option>
              <option value="zero">Zero Rated</option>
              <option value="exempted">Exempted</option>
            </select>
          </div>
        </div>

        <hr />

        <!-- Add items typeahead -->
        <div class="form-group">
          <label >Add Item</label>
          <div class="position-relative">
            <input
              type="text"
              class="form-control"
              v-model="inventorySearch.term"
              @input="onInventoryType"
              @keydown.down.prevent="focusNextSuggestion"
              @keydown.up.prevent="focusPrevSuggestion"
              @keydown.enter.prevent="selectHighlightedSuggestion"
              placeholder="Type item name to add..."
              autocomplete="off"
            />
            <!-- suggestions dropdown -->
            <div v-if="inventorySearch.show && inventorySearch.suggestions.length" class="dropdown-menu show w-100" style="z-index: 2000;">
              <button
                v-for="(s, idx) in inventorySearch.suggestions"
                :key="s.id"
                type="button"
                class="dropdown-item d-flex justify-content-between align-items-center"
                :class="{ active: inventorySearch.highlightIndex === idx }"
                @mousedown.prevent="addInventoryItemFromSuggestion(s)"
              >
                <div>
                  <div>{{ s.product }}</div>
                  <small class="text-muted d-block">{{ s.sku || '' }}</small>
                </div>
                <small :class="stockBadgeClass(s.remaining_units)">{{ s.remaining_units }} left</small>
              </button>
            </div>
          </div>
        </div>

        <!-- Selected items list -->
        <div v-if="form.items.length" class="mb-3">
          <div class="list-group">
            <div v-for="(itm, idx) in form.items" :key="itm.temp_uid || itm.id" class="list-group-item">
              <div class="d-flex align-items-start">
                <div class="mr-3" style="width: 42%">
                  <strong class="d-block text-capitalize">{{ itm.product.product }}</strong>
                  <small class="text-muted">{{ itm.product.sku || '' }}</small>
                </div>

                <div class="mr-3 d-flex flex-column" style="width: 28%">
                  <label class="mb-1">Qty</label>
                  <input type="number" min="1" class="form-control form-control-sm" v-model.number="itm.count" />
                  <small class="text-muted mt-1">Remaining: <span :class="stockBadgeClass(itm.product.remaining_units)">{{ itm.product.remaining_units }}</span></small>
                </div>

                <div class="mr-3" style="width: 20%">
                  <label class="mb-1">Required?</label>
                  <div>
                    <div class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" :id="'req_'+idx" v-model="itm.is_required" />
                      <label class="custom-control-label" :for="'req_'+idx">{{ itm.is_required ? 'Yes' : 'No' }}</label>
                    </div>
                  </div>
                </div>

                <div class="ml-auto text-right">
                  <button class="btn btn-outline-secondary btn-sm mb-2" @click.prevent="toggleSubstitutes(itm)">{{ itm.showSubstitutes ? 'Hide' : 'Substitutes' }}</button>
                  <button class="btn btn-danger btn-sm" @click.prevent="removeItemFromForm(idx)"><i class="bi bi-x"></i></button>
                </div>
              </div>

              <!-- Substitutes area -->
              <div v-if="itm.showSubstitutes" class="mt-3 pl-4">

                <div v-for="(sub, sIdx) in itm.substitutes" :key="sub.temp_uid || sub.id" class="d-flex align-items-center mb-2">

                    <input type="text"
                         class="form-control form-control-sm mr-2"
                         v-model="sub.name"
                         @input="onSubstituteType(itm, sub, sIdx)"
                         placeholder="Search substitute..."
                         style="flex:1" />
                  <input type="number" min="1" class="form-control form-control-sm mr-2" v-model.number="sub.count" style="width:90px" />
                  <select v-model.number="sub.priority" class="form-control form-control-sm mr-2" style="width:110px">
                    <option v-for="n in 5" :key="n" :value="n">Priority {{ n }}</option>
                  </select>
                  <button class="btn btn-sm btn-outline-danger" @click.prevent="removeSubstitute(itm, sIdx)"><i class="bi bi-x"></i></button>

                  <!-- substitute suggestions dropdown -->
                  <div v-if="sub.showDropdown && sub.suggestions && sub.suggestions.length" class="dropdown-menu show" style="position:absolute; z-index:2100; max-height:220px; overflow:auto;">
                    <button v-for="opt in sub.suggestions" :key="opt.id" class="dropdown-item" @mousedown.prevent="selectSubstitute(itm, sub, opt)">
                      {{ opt.product }} <small class="text-muted d-block">{{ opt.sku || '' }}</small>
                    </button>
                  </div>
                </div>

                <div>
                  <button class="btn btn-sm btn-outline-primary" @click.prevent="addSubstitute(itm)" :disabled="(itm.substitutes||[]).length >= 5">
                    + Add Substitute
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center text-muted mb-3">No items added to this group yet. Use the search box above to add.</div>

        <!-- Settings area -->
        <hr />
        <div class="form-row align-items-center">
          <div class="form-group col-md-6">
            <label class="mb-1">Allow group sale if required items missing?</label>
            <select class="form-control" v-model="form.settings.allow_missing_required">
              <option :value="false">No — Block sale</option>
              <option :value="true">Yes — Allow sale</option>
            </select>
          </div>

          <div class="form-group col-md-6">
            <label class="mb-1">Deduction strategy</label>
            <select class="form-control" v-model="form.settings.deduction_strategy">
              <option value="original_first">Original first, then substitutes</option>
              <option value="substitute_first">Use substitutes first</option>
              <option value="most_stock">Use the item with most stock</option>
            </select>
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex justify-content-between mt-3">
          <div>
            <button type="button" class="btn btn-outline-secondary" @click="resetForm">Reset</button>
            <button v-if="isEditing && canManageInventory" type="button" class="btn btn-danger ml-2" @click="deleteProd(form.id)">Delete Group</button>
          </div>
          <div>
            <button type="button" class="btn btn-secondary mr-2" @click="$refs.groupModal.hide()">Cancel</button>
            <button type="submit" class="btn btn-primary">{{ isEditing ? 'Save Changes' : 'Create Group' }}</button>
          </div>
        </div>
      </form>
    </b-modal>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import moment from 'moment';
import debounce from 'lodash/debounce';

export default {
  name: 'GroupedItemsManager',
  data() {
    return {
      // main table state
      searchItems: '',
      items: [],
      fields: [
        { key: 'title', label: 'Title', sortable: true },
        { key: 'items_count', label: 'Items', sortable: true },
        { key: 'selling_price', label: 'Retail', sortable: true },
        { key: 'wholesale', label: 'Wholesale', sortable: true }
      ],

      // settings & user
      companySettings: {},
      userRole: '',
      selectedProduct: null,

      // inventory search/state
      inventoryItemsAll: [], // loaded once
      inventorySearch: {
        term: '',
        show: false,
        suggestions: [],
        highlightIndex: 0
      },

      // form for creating/updating group
      form: this.emptyForm(),

      // convenience for determining mode
      isEditing: false
    };
  },

  computed: {
    ...mapGetters(['userDetails']),
    canManageInventory() {
      // keep same permission function you used
      if (!this.userDetails) return false;
      return this.userDetails.roles && this.userDetails.roles[0] && this.userDetails.roles[0].name === 'Admin'
        || this.userDetails.permissions && this.userDetails.permissions.some(p => p.name === 'can manage inventory');
    }
  },

  watch: {
    // keep a local debounced loader for search box
    searchItems(val) {
      // nothing immediate — debounce does the job via onSearchInput
    }
  },

  mounted() {
    this.getCompanySettings();
    this.getProducts(); // loads all inventory items for search/substitutes
    this.getGroupedProducts();
    this.getUserDetails();
  },

  methods: {
    // ---------- UTILITIES ----------
    emptyForm() {
      return {
        id: null,
        group_title: '',
        cost: null,
        wholesale: null,
        vat_category: 'standard',
        is_active: true,
        items: [], // each: { id, product: {id,product,sku,remaining_units}, count, is_required, substitutes:[], showSubstitutes}
        settings: {
          allow_missing_required: false,
          deduction_strategy: 'original_first'
        }
      };
    },

    formatCurrency(val) {
      if (val === null || val === undefined) return 'KES 0.00';
      const n = parseFloat(val) || 0;
      return `KES ${n.toFixed(2)}`;
    },

    formatDate(date, fmt = 'DD-MM-YYYY') {
      return date ? moment(date).format(fmt) : '-';
    },

    stockBadgeClass(units) {
      if (units === null || units === undefined) return 'text-muted';
    //   if (units <= 0) return 'text-danger';
    //   if (units <= 5) return 'text-dark';
      return 'text-dark';
    },

    // ---------- API loaders ----------
    async getCompanySettings() {
      try {
        const res = await axios.get('/api/settings');
        this.companySettings = res.data || {};
      } catch (e) { /* silent */ }
    },

    async getProducts() {
      // loads all inventory items used for typeahead & substitutes
      try {
        const res = await axios.get('/api/all-items-and-count');
        this.inventoryItemsAll = res.data.items || [];
      } catch (e) { console.error(e); }
    },

    async getUserDetails() {
      try {
        const res = await axios.get('/api/user');
        this.userRole = res.data.roles && res.data.roles[0] ? res.data.roles[0].name : '';
        // userDetails in Vuex should remain available
      } catch (e) { /* silent */ }
    },

async getGroupedProducts() {
  try {
    const res = await axios.get('/api/grouped-inventory', {
      params: { search: this.searchItems }
    });

    const payload = Array.isArray(res.data) ? res.data : [];

    const arr = payload.map(value => {
      if (!value || typeof value !== 'object') return null;

      let settings = value.settings;

      // ✅ Ensure settings is always an object
      if (typeof settings === 'string') {
        try {
          settings = JSON.parse(settings);
        } catch (err) {
          console.warn('Failed to parse settings, using defaults:', err);
          settings = {
            allow_missing_required: false,
            deduction_strategy: 'original_first'
          };
        }
      }

      // If it's null or not an object, set default
      if (!settings || typeof settings !== 'object') {
        settings = {
          allow_missing_required: false,
          deduction_strategy: 'original_first'
        };
      }

      return {
        id: value.id,
        title: value.group_title,
        items: value.grouped_items || [],
        items_count: (value.grouped_items || []).length,
        wholesale: value.wholesale,
        selling_price: value.cost,
        settings, // ✅ now always an object
        allOtherItems: value
      };
    }).filter(Boolean); // Remove nulls

    this.items = arr;
  } catch (e) {
    console.error('Error fetching grouped products:', e);
  }
},

    // ---------- Search (debounced) ----------
    onSearchInput: debounce(function () {
      this.getGroupedProducts();
    }, 350),

    // ---------- Row click ----------
    onRowClick(item) {
      if (!item) return;
      this.selectedProduct = item;
      // show details modal
      this.$refs.itemDetailsModal.show();
    },

    // ---------- Create/Edit flows ----------
    openCreateModal() {
      this.isEditing = false;
      this.form = this.emptyForm();
      this.$refs.groupModal.show();
    },

  openEditModal() {
  const other = this.selectedProduct.allOtherItems;

  // ✅ Ensure settings is always an object
  let settings = other.settings;
  if (typeof settings === 'string') {
    try {
      settings = JSON.parse(settings);
    } catch (err) {
      console.warn('Failed to parse settings in openEditModal:', err);
      settings = {
        allow_missing_required: false,
        deduction_strategy: 'original_first'
      };
    }
  }

  if (!settings || typeof settings !== 'object') {
    settings = {
      allow_missing_required: false,
      deduction_strategy: 'original_first'
    };
  }

  this.isEditing = true;
  this.form = {
    id: other.id,
    group_title: other.group_title,
    cost: other.cost,
    wholesale: other.wholesale,
    vat_category: other.vat_category || 'standard',
    is_active: other.is_active !== undefined ? other.is_active : true,
    items: (other.grouped_items || []).map(it => ({
      id: it.id,
      product: it.product,
      count: it.count || 1,
      is_required: it.is_required !== undefined ? it.is_required : true,
      substitutes: (it.substitutes || []).map(s => ({
        id: s.id,
        name: s.product.product,
        count: s.count || 1,
        priority: s.priority || 1,
        temp_uid: s.id ? null : this._tempUid()
      })),
      showSubstitutes: false,
      temp_uid: it.id ? null : this._tempUid()
    })),
    settings // ✅ Always an object now
  };

  this.$refs.groupModal.show();
},

    // small uid for temporary items (frontend only)
    _tempUid() {
      return `t_${Math.random().toString(36).slice(2, 9)}`;
    },

    // ---------- Inventory typeahead & selection ----------
    onInventoryType: debounce(function () {
      const term = (this.inventorySearch.term || '').trim().toLowerCase();
      if (!term) {
        this.inventorySearch.show = false;
        this.inventorySearch.suggestions = [];
        return;
      }
      // local filter - could change to server-side search if dataset is big
      const suggestions = this.inventoryItemsAll.filter(it => (it.product || '').toLowerCase().includes(term));
      this.inventorySearch.suggestions = suggestions.slice(0, 12);
      this.inventorySearch.show = suggestions.length > 0;
      this.inventorySearch.highlightIndex = 0;
    }, 200),

    focusNextSuggestion() {
      if (!this.inventorySearch.show) return;
      const max = this.inventorySearch.suggestions.length - 1;
      this.inventorySearch.highlightIndex = Math.min(max, this.inventorySearch.highlightIndex + 1);
    },

    focusPrevSuggestion() {
      if (!this.inventorySearch.show) return;
      this.inventorySearch.highlightIndex = Math.max(0, this.inventorySearch.highlightIndex - 1);
    },

    selectHighlightedSuggestion() {
      const sel = this.inventorySearch.suggestions[this.inventorySearch.highlightIndex];
      if (sel) this.addInventoryItemFromSuggestion(sel);
    },

    addInventoryItemFromSuggestion(item) {
      // push item into form.items (avoid duplicates)
      const exists = this.form.items.some(i => i.product && i.product.id === item.id);
      if (!exists) {
        this.form.items.push({
          id: null, // group-unit id (if editing existing)
          product: Object.assign({}, item),
          count: 1,
          is_required: true,
          substitutes: [],
          showSubstitutes: false,
          temp_uid: this._tempUid()
        });
      } else {
        // bump count visually
        const found = this.form.items.find(i => i.product.id === item.id);
        found.count = (found.count || 0) + 1;
      }
      this.inventorySearch.term = '';
      this.inventorySearch.show = false;
      this.inventorySearch.suggestions = [];
    },

    // ---------- Form item management ----------
    removeItemFromForm(index) {
      this.form.items.splice(index, 1);
    },

    toggleSubstitutes(item) {
      this.$set(item, 'showSubstitutes', !item.showSubstitutes);
      if (!item.substitutes) this.$set(item, 'substitutes', []);
    },

    addSubstitute(item) {
      if (!item.substitutes) this.$set(item, 'substitutes', []);
      if (item.substitutes.length >= 5) return;
      item.substitutes.push({
        id: null,
        name: '',
        count: 1,
        priority: item.substitutes.length + 1,
        suggestions: [],
        showDropdown: false,
        temp_uid: this._tempUid()
      });
    },

    removeSubstitute(item, idx) {
      item.substitutes.splice(idx, 1);
    },

    onSubstituteType(item, sub, sIdx) {
      const q = (sub.name || '').trim().toLowerCase();
      sub.showDropdown = !!q;
      if (!q) {
        sub.suggestions = [];
        return;
      }
      // filter from inventory list
      sub.suggestions = this.inventoryItemsAll.filter(it => (it.product||'').toLowerCase().includes(q)).slice(0, 10);
    },

    selectSubstitute(item, sub, opt) {
      sub.name = opt.product;
      sub.id = opt.id;
      sub.showDropdown = false;
      sub.suggestions = [];
    },

    // ---------- Submit create / edit ----------
    async submitGroupForm() {
      // Basic validation
      if (!this.form.group_title || !this.form.items.length) {
        this.$toasts.push({ type: 'danger', message: 'Provide a group title and at least one item.' });
        return;
      }

      // Prepare payload consistent with your backend expectations
      const payload = {
        ...this.form,
        grouped_items: this.form.items.map(it => ({
          id: it.id || null,
          product_id: it.product.id,
          count: it.count,
          is_required: !!it.is_required,
          substitutes: (it.substitutes || []).map(s => ({
            product_id: s.id,
            product_name: s.name,
            count: s.count,
            priority: s.priority
          }))
        }))
      };

      try {
        if (this.isEditing) {
          await axios.put('/api/group-item', payload);
          this.$toasts.push({ type: 'success', message: 'Grouped Item updated successfully' });
        } else {
          await axios.post('/api/group-item', payload);
          this.$toasts.push({ type: 'success', message: 'Grouped Item created successfully' });
        }
        this.$refs.groupModal.hide();
        this.getGroupedProducts();
      } catch (err) {
        console.error(err);
        this.$toasts.push({ type: 'danger', message: (err.response && err.response.data && err.response.data.message) || 'Request failed' });
      }
    },

    // ---------- Delete endpoints ----------
    async deleteProd(item_id) {
      if (!confirm('Are you sure you want to delete this grouped product?')) return;
      try {
        await axios.delete('/api/group-item', { params: { id: item_id } });
        this.$toasts.push({ type: 'success', message: 'Grouped Item deleted' });
        this.$refs.groupModal.hide();
        this.$refs.itemDetailsModal.hide();
        this.getGroupedProducts();
      } catch (e) {
        console.error(e);
        this.$toasts.push({ type: 'danger', message: 'Delete failed' });
      }
    },

    async deleteGroupedItem(groupitem_id) {
      try {
        await axios.delete('/api/group-item-unit', { params: { id: groupitem_id } });
        this.$toasts.push({ type: 'success', message: 'Grouped item removed' });
        // mutate local selectedProduct to reflect deletion
        if (this.selectedProduct && this.selectedProduct.allOtherItems) {
          const filtered = (this.selectedProduct.allOtherItems.grouped_items || []).filter(i => i.id !== groupitem_id);
          this.selectedProduct.allOtherItems.grouped_items = filtered;
        }
      } catch (e) {
        console.error(e);
        this.$toasts.push({ type: 'danger', message: 'Failed to remove grouped item' });
      }
    },

    resetForm() {
      this.form = this.emptyForm();
      this.isEditing = false;
    },

    // ---------- small keyboard helpers ----------
    addInventoryItemFromSuggestionShortcut(index) {
      const s = this.inventorySearch.suggestions[index];
      if (s) this.addInventoryItemFromSuggestion(s);
    }
  }
};
</script>

<style scoped>
.grouped-items-component .dropdown-menu.show { max-height: 300px; overflow:auto; }
.list-group-item { background: #fff; }
.stock-badge { font-weight: 600; }
.dropdown-item.active, .dropdown-item:active {
  color: #000;
  text-decoration: none;
  background-color: #fd47196f;
}
</style>
